// The compliance config uses `Infinity` for "unlimited" limits, which JSON cannot represent
// (JSON.stringify turns it into null). When the config is delivered from the API to the browser
// through the react-env / window.reactEnv channel (which JSON-stringifies), we sentinel-encode
// Infinity on the way out and decode it on the way in. Both ends are first-party and we control
// them, so the sentinel convention can't drift.

// Distinctive, namespaced sentinels so they can't collide with a real string field in the
// config (e.g. a free-text `message`).
const POSITIVE_INFINITY_SENTINEL = '__ECOMM_COMPLIANCE_POSITIVE_INFINITY__';
const NEGATIVE_INFINITY_SENTINEL = '__ECOMM_COMPLIANCE_NEGATIVE_INFINITY__';

const deepTransform = (value, transformLeaf) => {
  // `ancestors` tracks the current recursion path so a circular reference throws a clear error
  // instead of overflowing the stack. It's a path set (removed on unwind), so shared but acyclic
  // references (DAGs) don't false-positive.
  const walk = (node, ancestors) => {
    if (node && typeof node === 'object') {
      if (ancestors.has(node)) {
        throw new Error('compliance-serialization: circular reference detected');
      }
      ancestors.add(node);
      const result = Array.isArray(node)
        ? node.map((item) => walk(item, ancestors))
        : Object.keys(node).reduce((acc, key) => {
            acc[key] = walk(node[key], ancestors);
            return acc;
          }, {});
      ancestors.delete(node);
      return result;
    }
    return transformLeaf(node);
  };
  return walk(value, new Set());
};

export const encodeComplianceConfig = (config) =>
  deepTransform(config, (leaf) => {
    if (leaf === Infinity) {
      return POSITIVE_INFINITY_SENTINEL;
    }
    if (leaf === -Infinity) {
      return NEGATIVE_INFINITY_SENTINEL;
    }
    return leaf;
  });

export const decodeComplianceConfig = (config) =>
  deepTransform(config, (leaf) => {
    if (leaf === POSITIVE_INFINITY_SENTINEL) {
      return Infinity;
    }
    if (leaf === NEGATIVE_INFINITY_SENTINEL) {
      return -Infinity;
    }
    return leaf;
  });
